import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';

import { BasePage } from '../../base.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import {
    ContractInterface,
    ContractSignatureType,
    GetOperationUseCase,
    GetSelectedProductSignatoriesUseCase,
    OrderClientInterface,
    OrderType,
    RemoveOrderUseCase,
    SelectedProductInterface,
    SignOrderUseCase,
    Transaction,
    UpdateOrderUseCase,
} from '../../../../core';
import { Id } from '../../../domain/data';
import { Loader } from '../../../client/helpers/loader';
import { LocalStorage } from '../../../client/helpers/local-storage';
import { MainRoutes } from '../../../main-routes.enum';
import { OperativePage } from '../operative.page';
import { ParentEvents } from '../../../utils/parent-events.service';

import { environment } from '../../../../../environments/environment';
import { ConfirmSignatureDialog } from 'src/app/main/ui/dialog/confirm-signature/confirm-signature.dialog';
import moment from 'moment';

@Component({
    templateUrl: './operative-sign.page.html',
    styleUrls: ['./operative-sign.page.scss'],
})
export class OperativeSignPage extends BasePage implements AfterViewInit, OnDestroy, OnInit {
    public OrderType: typeof OrderType = OrderType;

    private backSubscription: Subscription;

    public participants: OrderClientInterface[] = [];
    public contract: ContractInterface;
    public selectedProduct: SelectedProductInterface;
    public isClientPortal = environment.portalClient;
    public participantsPhoneList = [];
    public meet:string;

    constructor(
        operativePage: OperativePage,
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getOperationUseCase: GetOperationUseCase,
        private getSelectedProductSignatoriesUseCase: GetSelectedProductSignatoriesUseCase,
        private loader: Loader,
        private localStorage: LocalStorage,
        private removeOrderUseCase: RemoveOrderUseCase,
        private route: ActivatedRoute,
        private router: Router,
        private signOrderUseCase: SignOrderUseCase,
        private updateOrderUseCase: UpdateOrderUseCase
    ) {
        super(parentEvents);
        operativePage.showBackButton();
        this.backSubscription = operativePage.back.subscribe(() => {
            window.history.back();
        });
    }

    get signedBy(): 'all' | 'one' {
        if (
            this.contract &&
            (this.contract.signatureType === ContractSignatureType.JOINT ||
                this.contract.signatureType === ContractSignatureType.CONDITIONAL_JOINT)
        ) {
            return 'all';
        }

        return 'one';
    }

    get allowDigital(): boolean {
        return !this.contract || this.contract.signatureType !== ContractSignatureType.CONDITIONAL_JOINT;
    }

    get allowHandwritten(): boolean {
        return !this.isClientPortal;
    }

    ngOnInit() {
        this.getSignatories();
    }

    private getSignatories() {
        this.getOperationUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.contract = transaction.data.contract;
                this.selectedProduct = transaction.data.selectedProduct;
                this.getSelectedProductSignatoriesUseCase
                    .bind({
                        product: this.selectedProduct,
                        clientCode: Id.fromString(this.localStorage.clientCode),
                    })
                    .subscribe((transactionSignatories) => {
                        transactionSignatories.fold(
                            (signatories) => {
                                if (!signatories || signatories.length === 0) {
                                    this.dialog.open(ErrorDialog, {
                                        data: {
                                            text: 'dialog.operate.cannot-sign',
                                        },
                                        disableClose: true,
                                    });
                                }
                                this.participants = signatories;
                            },
                            (_) =>
                                this.dialog.open(ErrorDialog, {
                                    data: {
                                        text: 'error.selected-product-not-signatories',
                                    },
                                })
                        );
                    });
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: 'error.selected-product-not-signatories' },
                });
            }
        });
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onSubmit() {
        this.selectedProduct.order.clients = this.participants.filter((participant) => !!participant.signatureType);
        let dialogRef: any = [];

        let participantsPhone = '';
        let signOK = false;

        this.participants.forEach((participant, i) => {
            if (this.participants[i].signatureType !== 'handwritten_signature') {
                signOK = true;
            if (this.participants[i].phone !== undefined) {
                this.participantsPhoneList.push(participant.phone);
            }
        }
        });

        this.participantsPhoneList.forEach((item, i) => {
            if(i == this.participantsPhoneList.length-2){
            participantsPhone += this.participantsPhoneList[i] + ' y ';
            }
            else{
                participantsPhone += this.participantsPhoneList[i] + ', ';
            }
            if(i == this.participantsPhoneList.length-1){
                participantsPhone = participantsPhone.substring(0, participantsPhone.length-2);
            }
        });

        if (signOK) {
            if (this.participants.length === 1) {
                dialogRef = this.dialog.open(ConfirmSignatureDialog, {
                    data: {
                        participants: this.participants,
                        phone: this.participants[0].phone,
                        single: true,
                    },
                });
            } else {
                dialogRef = this.dialog.open(ConfirmSignatureDialog, {
                    data: {
                        participants: this.participants,
                        phone: participantsPhone,
                        single: false,
                    },
                });
            }
            dialogRef.afterClosed().subscribe((result) => {
                this.participantsPhoneList = [];
                if (result) {
                    this.route.queryParams.subscribe((params) => {
                        let observable: Observable<Transaction<any>> = this.signOrderUseCase.bind({ product: this.selectedProduct });

                        if ('update' in params && params.update === '1') {
                            observable = this.updateOrderUseCase.bind({
                                product: this.selectedProduct,
                            });
                        } else if ('cancel' in params && params.cancel === '1') {
                            observable = this.removeOrderUseCase.bind({
                                product: this.selectedProduct,
                            });
                        }

                        observable.subscribe((transaction) => {
                            transaction.fold(
                                () => {
                                    const section = this.selectedProduct.isSubscriptionActive() ? 'period' : 'pending';
                                    this.router.navigateByUrl(MainRoutes.OPERATIVE_LIST + `?section=${section}`);
                                },
                                (throwable) =>
                                    this.dialog.open(ErrorDialog, {
                                        data: { text: transaction.error },
                                    })
                            );
                        });
                    });
                }
            });
        } else {
            this.route.queryParams.subscribe((params) => {
                let observable: Observable<Transaction<any>> = this.signOrderUseCase.bind({ product: this.selectedProduct });
                if ('update' in params && params.update === '1') {
                    observable = this.updateOrderUseCase.bind({
                        product: this.selectedProduct,
                    });
                } else if ('cancel' in params && params.cancel === '1') {
                    observable = this.removeOrderUseCase.bind({
                        product: this.selectedProduct,
                    });
                }
                observable.subscribe((transaction) => {
                    transaction.fold(
                        () => {
                            const section = this.selectedProduct.isSubscriptionActive() ? 'period' : 'pending';
                            this.router.navigateByUrl(MainRoutes.OPERATIVE_LIST + `?section=${section}`);
                        },
                        (throwable) =>
                            this.dialog.open(ErrorDialog, {
                                data: { text: transaction.error },
                            })
                    );
                });
            });
        }
    }
}
